/**
 * Design GLB layer: loads user CAD models (GLB format) with georeferencing.
 *
 * Georeferencing:
 * - design_origin.json in user folder: { "origin": [lon, lat], "sizeScale?": 1 }
 * - CAD origin (0,0) maps to that geographic point
 * - CAD assumed to use meters; METER_OFFSETS handles conversion
 *
 * Meters-per-degree (for project areas ≤10km, centroid variation is negligible):
 * - 1° lat ≈ 111.32 km
 * - 1° lon ≈ 111.32 * cos(lat) km
 *
 * GLB loading: deck.gl uses load() from @loaders.gl/core for URL strings.
 * We must register GLTFLoader so .glb URLs can be parsed. Using 2.2.x to match deck.gl.
 *
 * Color-only materials: Materials with baseColorFactor but no baseColorTexture
 * render white with flat lighting. We inject a 1x1 solid-color texture for each
 * such material so they display correctly.
 */
import { load, registerLoaders } from "@loaders.gl/core";
import { GLTFLoader } from "@loaders.gl/gltf";
import { ScenegraphLayer } from "@deck.gl/mesh-layers";

registerLoaders([GLTFLoader]);
import { MapboxLayer as DeckLayer } from "@deck.gl/mapbox";
import { PolygonLayer } from "@deck.gl/layers";
import { COORDINATE_SYSTEM } from "@deck.gl/core";
import GL from "@luma.gl/constants";
import * as turf from "@turf/turf";
import type { GeoJSON } from "@/models";

export const designGlbLayerId = "userDesignGlb";
export const designGlbDebugLayerId = "userDesignGlbDebug";

export interface DesignOrigin {
  origin: [number, number];
  sizeScale?: number;
}

/** Meters per degree of latitude (constant, ~111.32 km) */
export const METERS_PER_DEGREE_LAT = 111320;

/**
 * Meters per degree of longitude at a given latitude.
 * Uses cos(lat) since longitude lines converge at poles.
 */
export function metersPerDegreeLonAtLat(lat: number): number {
  return METERS_PER_DEGREE_LAT * Math.cos((lat * Math.PI) / 180);
}

/**
 * Get centroid [lon, lat] from project_area GeoJSON.
 * Used as reference for meters-per-degree (or fallback origin if needed).
 */
export function getProjectAreaCentroid(
  projectArea: GeoJSON
): [number, number] | null {
  try {
    const geo = projectArea as unknown as turf.helpers.AllGeoJSON;
    const [minX, minY, maxX, maxY] = turf.bbox(geo);
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  } catch {
    return null;
  }
}

/** glTF WebGL constants for sampler */
const GL_LINEAR = 0x2601;
const GL_NEAREST_MIPMAP_LINEAR = 0x2702;
const GL_REPEAT = 0x2901;

/** Default sampler for synthetic textures */
const DEFAULT_SAMPLER_OBJ = {
  parameters: {
    0x2800: GL_LINEAR,
    0x2801: GL_NEAREST_MIPMAP_LINEAR,
    0x2802: GL_REPEAT,
    0x2803: GL_REPEAT,
  },
};

/**
 * Load a GLB and inject 1x1 solid-color textures for materials that have
 * baseColorFactor but no baseColorTexture. This fixes color-only materials
 * which otherwise render white with flat lighting.
 */
export async function loadAndProcessDesignGlb(glbUrl: string): Promise<object> {
  const gltf = await load(glbUrl, GLTFLoader, {
    fetch: { mode: "cors" },
    gltf: { loadImages: true, loadBuffers: true, postProcess: true },
  });

  const json = gltf as {
    materials?: Array<{
      pbrMetallicRoughness?: {
        baseColorFactor?: number[];
        baseColorTexture?: { texture?: object };
      };
    }>;
    images?: Array<{ id?: string; image?: object }>;
    textures?: Array<{ source?: object; sampler?: object }>;
    samplers?: Array<object>;
  };

  if (!json.materials) return gltf;

  const sampler =
    json.samplers && json.samplers.length > 0
      ? json.samplers[0]
      : DEFAULT_SAMPLER_OBJ;

  let syntheticIndex = 0;
  for (const material of json.materials) {
    const pbr = material.pbrMetallicRoughness;
    if (!pbr || pbr.baseColorTexture) continue;

    const factor = pbr.baseColorFactor ?? [1, 1, 1, 1];
    const r = Math.round((factor[0] ?? 1) * 255);
    const g = Math.round((factor[1] ?? 1) * 255);
    const b = Math.round((factor[2] ?? 1) * 255);
    const a = Math.round((factor[3] ?? 1) * 255);

    // Use ImageData - valid texImage2D source (our {data,width,height} was not)
    const imageData = new ImageData(1, 1);
    imageData.data[0] = r;
    imageData.data[1] = g;
    imageData.data[2] = b;
    imageData.data[3] = a;

    const syntheticImage = {
      id: `synthetic-basecolor-${syntheticIndex}`,
      image: imageData,
    };

    if (!json.images) json.images = [];
    json.images.push(syntheticImage);

    const syntheticTexture = {
      id: `synthetic-tex-${syntheticIndex}`,
      source: syntheticImage,
      sampler,
    };

    if (!json.textures) json.textures = [];
    json.textures.push(syntheticTexture);

    pbr.baseColorTexture = { texture: syntheticTexture } as {
      texture?: object;
    };
    pbr.baseColorFactor = [1, 1, 1, 1];

    syntheticIndex += 1;
  }

  return gltf;
}

/** Design GLB layer props for MapboxOverlay (interleaved mode) */
export interface DesignGlbLayerProps {
  id: string;
  type: typeof ScenegraphLayer;
  data: Array<{ position: number[] }>;
  scenegraph: Promise<object>;
  getPosition: (d: { position: number[] }) => number[];
  getOrientation: number[];
  coordinateSystem: number;
  coordinateOrigin: [number, number, number];
  sizeScale: number;
  pickable: boolean;
  visible: boolean;
  _lighting: string;
  loadOptions: object;
  onDataLoad?: () => void;
  onError?: (err: Error) => void;
}

/**
 * Build design GLB layer for MapboxOverlay (interleaved mode).
 * Returns an actual ScenegraphLayer instance; MapboxOverlay expects Layer instances with .props.
 * Use with addDeckLayerToOverlay for proper depth occlusion with buildings.
 */
export function buildDesignGlbLayerForOverlay(
  glbUrl: string,
  origin: [number, number],
  sizeScale = 10
): InstanceType<typeof ScenegraphLayer> {
  const coordinateOrigin: [number, number, number] = [origin[0], origin[1], 0];
  return new ScenegraphLayer({
    id: designGlbLayerId,
    data: [{ position: [0, 0, 0] as [number, number, number] }],
    scenegraph: loadAndProcessDesignGlb(glbUrl) as any,
    getPosition: (d: { position: [number, number, number] }) => d.position,
    getOrientation: [0, 0, 90],
    coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
    coordinateOrigin,
    sizeScale,
    pickable: false,
    visible: true,
    _lighting: "flat",
    loadOptions: { fetch: { mode: "cors" } },
    onDataLoad:
      process.env.NODE_ENV === "development"
        ? () => console.log("Design GLB loaded:", glbUrl)
        : undefined,
    onError: (err: Error) =>
      console.error("Design GLB load error:", glbUrl, err),
    // Interleaved: draw design before buildings so semi-transparent buildings blend with it
    beforeId: "groundfloor",
  } as any);
}

/**
 * Build a Deck.gl ScenegraphLayer for the user's design GLB (legacy MapboxLayer).
 * Use buildDesignGlbLayerForOverlay + addDeckLayerToOverlay for interleaved mode.
 *
 * @param glbUrl - URL to the GLB file (e.g. from getLayerAssetUrl)
 * @param origin - [lon, lat] from design_origin.json (CAD origin in geo coords)
 * @param sizeScale - Scale factor; larger = bigger model (default 10; tune per CAD export)
 */
export function buildDesignGlbLayer(
  glbUrl: string,
  origin: [number, number],
  sizeScale = 10
): DeckLayer<any> {
  const coordinateOrigin: [number, number, number] = [origin[0], origin[1], 0];

  return new DeckLayer({
    id: designGlbLayerId,
    type: ScenegraphLayer,
    // z=5 lifts model slightly above ground to avoid clipping; tune if needed
    data: [{ position: [0, 0, 0] }],
    scenegraph: loadAndProcessDesignGlb(glbUrl),
    getPosition: (d: { position: number[] }) => d.position,
    getOrientation: [0, 0, 90],
    coordinateSystem: COORDINATE_SYSTEM.METER_OFFSETS,
    coordinateOrigin,
    sizeScale,
    pickable: false,
    visible: true,
    _lighting: "flat",
    loadOptions: {
      fetch: {
        mode: "cors",
      },
    },
    // Depth enabled: with MapboxOverlay interleaved mode, depth aligns with MapLibre buildings
    onDataLoad: () => {
      if (process.env.NODE_ENV === "development") {
        console.log("Design GLB loaded successfully:", glbUrl);
      }
    },
    onError: (err: Error) => {
      console.error("Design GLB load error:", glbUrl, err);
    },
  } as any);
}

/**
 * Debug layer: a small extruded box at the design origin to verify positioning.
 * Remove or disable when GLB rendering works.
 */
export function buildDesignGlbDebugLayer(
  origin: [number, number]
): DeckLayer<any> {
  const [lon, lat] = origin;
  const d = 0.00015;
  const polygon = [
    [lon - d, lat - d],
    [lon + d, lat - d],
    [lon + d, lat + d],
    [lon - d, lat + d],
    [lon - d, lat - d],
  ] as [number, number][];

  return new DeckLayer({
    id: designGlbDebugLayerId,
    type: PolygonLayer,
    data: [{ polygon }],
    getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
    getFillColor: [255, 0, 0],
    getElevation: 80,
    extruded: true,
    pickable: false,
    visible: true,
    // Disable depth test so the debug box always draws on top (no occlusion)
    parameters: {
      [GL.DEPTH_TEST]: false,
      [GL.DEPTH_WRITEMASK]: false,
    },
  } as any);
}
